import React, { useEffect, useState, useRef, useReducer } from 'react'
import useSocket from 'use-socket.io-client'
import cn from 'classnames'
import { useLocation } from 'react-router-dom'
import { formatDistanceToNow } from 'date-fns'
import SecureButton from './SecureButton'
import * as Icon from './Icons'
import { useOnClickOutside } from './hooks'
import { authRequest } from './api'
import TextWithTemplate from './TextWithTemplate'
import { reducer } from './RequestDetail'

import styles from './modals.module.css'

export function ChatModal({ userId, id }) {
  let location = useLocation()
  const [, , , requestType, requestId] = location.pathname.split('/')
  const [chat, setChat] = useState([])
  const ref = useRef()
  const messagesRef = useRef(null)
  const textareaRef = useRef(null)
  const [editingMessage, setEditingMessage] = useState(null)
  const [state, dispatch] = useReducer(reducer, {
    sending: false,
    sent: false,
    error: false,
  })

  const [replyContent, setReplyContent] = useState('')
  useOnClickOutside(ref, () => setEditingMessage(null))

  const kind = id ? (id.includes('quote') ? 'quote' : 'supervision') : 'generic'

  const [socket] = useSocket(`/`, {
    autoConnect: false,
  })

  useEffect(() => {
    async function updateChatInfo() {
      return await authRequest(
        `/admin/chat?userId=${userId}&kind=${kind}&id=${id}`
      )
        .then(setChat)
        .catch(console.log)
        .finally(() => {
          if (messagesRef && messagesRef.current) {
            messagesRef.current.scrollTop = messagesRef.current.scrollHeight
          }
        })
    }

    updateChatInfo()

    socket.connect()
    socket.on('connect', function () {
      socket.emit('room', userId)
    })
    socket.on('chat-msg-from-client', updateChatInfo)
    return () => socket.disconnect()
  }, [userId, id, kind, socket])

  useEffect(() => {
    textareaRef.current.focus()
  }, [])

  function replyHandler() {
    if (replyContent !== '') {
      authRequest(`/admin/chat`, {
        method: 'POST',
        body: JSON.stringify({
          userId,
          id,
          kind,
          content: replyContent,
        }),
      })
        .then((messages) => {
          if (messages) {
            setChat(messages)
          }
        })
        .catch((err) => console.log({ err }))
        .finally(() => {
          setReplyContent('')
          if (messagesRef && messagesRef.current) {
            messagesRef.current.scrollTop = messagesRef.current.scrollHeight
          }
        })
    }
  }

  return (
    <div className="chat-wrapper">
      <ul className="chat-messages" ref={messagesRef}>
        {chat.map((message) => {
          const date = new Date(message.time)
          const selfMessage = message.author !== 'Me'
          const isEditing = editingMessage === message.id
          return (
            <div
              style={{ position: 'relative' }}
              className={cn(styles.chatMsg, {
                [styles.chatMsgSelf]: selfMessage,
              })}
              key={message.id}
              onDoubleClick={() => {
                setEditingMessage(message.id)
                setTimeout(() => {
                  ref.current.focus()
                }, 50)
              }}
            >
              {!isEditing && (
                <SecureButton
                  confirm={<Icon.Check className={styles.deleteChatMsgIcon} />}
                  className={styles.deleteChatMsg}
                  confirmClassName={cn(
                    styles.deleteChatMsg,
                    styles.deleteChatMsgConfirm
                  )}
                  type="button"
                  onConfirm={() => {
                    authRequest(`/admin/chat`, {
                      method: 'DELETE',
                      body: JSON.stringify({
                        userId,
                        id,
                        kind,
                        messageId: message.id,
                      }),
                    })
                  }}
                >
                  <Icon.Close className={styles.deleteChatMsgIcon} />
                </SecureButton>
              )}
              <small className={`chat-msg-meta`}>
                {selfMessage ? `${message.author}, ` : 'User, '}
                <span title={date}>
                  {formatDistanceToNow(date, {
                    includeSeconds: true,
                    addSuffix: true,
                  })}
                </span>
              </small>
              <p
                style={{
                  margin: 0,
                  outline: isEditing ? '1px dashed rgba(255,0,0,1)' : 0,
                }}
              >
                <span
                  contentEditable={isEditing}
                  ref={isEditing ? ref : null}
                  onKeyDown={(e) => {
                    if (e.keyCode === 13 && !e.shiftKey) {
                      e.preventDefault()
                      if (e.target.innerText !== '') {
                        authRequest(`/admin/chat`, {
                          method: 'PATCH',
                          body: JSON.stringify({
                            userId,
                            id,
                            kind,
                            messageId: message.id,
                            content: e.target.innerText,
                          }),
                        })
                        setEditingMessage(null)
                      }
                    }
                  }}
                >
                  {message.content}
                </span>
              </p>
            </div>
          )
        })}
      </ul>
      <form
        className="chat-form"
        onSubmit={(e) => {
          e.preventDefault()
          replyHandler()
        }}
      >
        <TextWithTemplate
          textContent={replyContent}
          setTextContent={setReplyContent}
          ref={textareaRef}
          kind="chat"
          className="chat-input"
          placeholder="Chat Message…"
          style={{ resize: 'none' }}
          onKeyDown={(e) => {
            if (e.keyCode === 13 && !e.shiftKey) {
              e.preventDefault()
              replyHandler()
            }
          }}
        />
        <button type="submit" className="chat-btn">
          Send
        </button>
      </form>
      {state.sent ? (
        <span
          className={cn(styles.notifButton, styles.message)}
          onClick={() => dispatch({ type: 'RESET' })}
        >
          Message sent successfully
        </span>
      ) : (
        <SecureButton
          type="button"
          disabled={state.sent}
          confirm={`Sure send Notification Email?`}
          className={cn(styles.notifButton, styles.orange)}
          confirmClassName={cn(styles.notifButton, styles.red)}
          onConfirm={async () => {
            if (
              window.confirm(
                `Last call: You are sending an email with the content of the "to-user-chat-notification" template?`
              )
            ) {
              dispatch({ type: 'SENDING' })
              authRequest(`/admin/email-notification`, {
                method: 'POST',
                body: JSON.stringify({
                  reason: 'chat',
                  id: userId,
                  requestType,
                  requestId,
                }),
              })
                .then(() => dispatch({ type: 'SENT' }))
                .catch((err) => dispatch({ type: 'ERROR', error: err.message }))
            }
          }}
        >
          {state.sending
            ? 'Sending Notification Message…'
            : 'Send Notification Message'}
        </SecureButton>
      )}
    </div>
  )
}

export const globalStyles = {
  overlay: (base) => ({
    ...base,
    backgroundColor: 'rgba(11, 10, 37, .95)',
  }),
  contentOuter: (base) => ({
    ...base,
    top: 10,
    right: 10,
    bottom: 10,
    left: 'auto',
    transform: 'translate(0, 0)',
    borderRadius: 8,
    width: 'calc(100% - 4em)',
    overflow: 'visible',
    [`@media (min-width: 1020px)`]: {
      width: '34vw',
    },
  }),
  contentInner: () => ({
    position: 'absolute',
    top: '1em',
    right: '1em',
    bottom: '1em',
    left: '1em',
    overflow: 'hidden',
  }),
  closeButton: (base) => ({
    ...base,
    top: '1em',
    left: '-2em',
    color: 'white',
  }),
}
